import Hls, { type ErrorData, ErrorTypes, Events } from "hls.js";
import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

interface UseHlsPlayerResult {
  isLoading: boolean;
  error: string | null;
  duration: number;
  /**
   * Manually re-arm the loader (drops the current HLS instance / src and
   * re-runs the init path). Used by the UI to recover from a stuck error
   * state — most commonly "HLS manifest not ready yet because the review
   * pipeline is still transcoding", which previously required a full page
   * refresh to unstick.
   */
  retry: () => void;
}

// HLS playlists for Ask Puntt video reviews are produced by the review
// pipeline asynchronously. The manifest URL is written to the ticket
// BEFORE the playlist itself is available on the CDN, so initial loads
// frequently 403/404 for a while. Retry aggressively enough to cover
// typical processing windows (~60s) instead of giving up in ~12s like
// the previous implementation, which stranded users on a "Download"
// fallback until they refreshed the page.
const MAX_RETRIES = 6;
const BASE_RETRY_MS = 2000;
const MAX_RETRY_MS = 10_000;

function canPlayNativeHls(video: HTMLVideoElement): boolean {
  // Safari / iOS. Chrome/Firefox return "" and MUST NOT be handed a raw
  // .m3u8 URL — setting video.src to a playlist they can't play fires a
  // MediaError, which we previously latched into a permanent error UI.
  return Boolean(video.canPlayType("application/vnd.apple.mpegurl"));
}

export function useHlsPlayer(
  videoRef: RefObject<HTMLVideoElement | null>,
  url: string,
): UseHlsPlayerResult {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  // Bump to force the init effect to re-run even though `url` didn't change
  // (manual "Retry" after the playlist becomes available).
  const [retryTick, setRetryTick] = useState(0);
  const hlsRef = useRef<Hls | null>(null);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const retry = useCallback(() => {
    setRetryTick((n) => n + 1);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return;

    setIsLoading(true);
    setError(null);
    setDuration(0);
    retryCountRef.current = 0;

    const handleMetadata = () => {
      setDuration(video.duration);
      setIsLoading(false);
    };

    const handleDurationChange = () => {
      if (video.duration && isFinite(video.duration)) {
        setDuration(video.duration);
      }
    };

    const handleError = () => {
      setError("Video playback failed");
      setIsLoading(false);
    };

    video.addEventListener("loadedmetadata", handleMetadata);
    video.addEventListener("durationchange", handleDurationChange);
    video.addEventListener("error", handleError);

    const isHlsUrl = /\.m3u8(\?|$)/i.test(url);

    const scheduleHlsRetry = (hls: Hls) => {
      const attempt = retryCountRef.current;
      const delay = Math.min(BASE_RETRY_MS * 2 ** (attempt - 1), MAX_RETRY_MS);
      retryTimeoutRef.current = setTimeout(() => {
        retryTimeoutRef.current = null;
        // startLoad() is the recommended way to retry after a fatal
        // NETWORK error — it reuses the existing manifest-loader config
        // and doesn't double-subscribe to events the way a bare
        // loadSource() retry does.
        hls.startLoad();
      }, delay);
    };

    if (isHlsUrl && Hls.isSupported()) {
      const hls = new Hls();
      hlsRef.current = hls;

      hls.on(Events.MANIFEST_PARSED, () => {
        // A successful manifest parse means we've recovered — clear the
        // retry budget so a later transient blip gets its own full retry
        // allowance instead of tipping us straight into the error state.
        retryCountRef.current = 0;
        setIsLoading(false);
      });

      hls.on(Events.ERROR, (_event, data: ErrorData) => {
        if (!data.fatal) return;

        if (data.type === ErrorTypes.MEDIA_ERROR) {
          // Try to recover decoder/buffer glitches without bouncing the
          // whole pipeline — this is cheaper than a full reload and
          // doesn't count against the network-retry budget.
          try {
            hls.recoverMediaError();
          } catch {
            setError("Video playback failed");
            setIsLoading(false);
          }
          return;
        }

        // Everything else (NETWORK_ERROR, OTHER_ERROR) → back off and
        // try the manifest again. This covers the common "playlist not
        // ready yet" case during review processing.
        retryCountRef.current += 1;
        if (retryCountRef.current <= MAX_RETRIES) {
          scheduleHlsRetry(hls);
        } else {
          // Give up loading, but DON'T hand the raw .m3u8 URL to a
          // video element that can't play it — that was the whole
          // source of the "stuck on Download" bug. On Safari we can
          // safely try native playback as a final fallback.
          hls.destroy();
          hlsRef.current = null;
          if (canPlayNativeHls(video)) {
            video.src = url;
            video.load();
          } else {
            setError("Video preview unavailable");
            setIsLoading(false);
          }
        }
      });

      hls.loadSource(url);
      hls.attachMedia(video);
    } else if (isHlsUrl && canPlayNativeHls(video)) {
      video.src = url;
      video.load();
    } else {
      video.src = url;
      video.load();
    }

    return () => {
      video.removeEventListener("loadedmetadata", handleMetadata);
      video.removeEventListener("durationchange", handleDurationChange);
      video.removeEventListener("error", handleError);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [url, retryTick]); // eslint-disable-line react-hooks/exhaustive-deps

  return { isLoading, error, duration, retry };
}
